import type { SubnavGroup } from './ui/Layout.tsx';

/**
 * The single-project view's six tabs and the parts inside them, shown as the
 * project's secondary nav (a group per tab, a link per part; on a narrow view
 * one picker). The URL carries both: `?tab=code&part=compute`.
 * The eleven old tab keys still open the right place (`?tab=roots` is Code,
 * Repositories), so links and bookmarks from before keep working.
 */
export type ProjectTab = 'overview' | 'work' | 'people' | 'code' | 'memory' | 'rules';

export const PROJECT_TABS: Array<{ id: ProjectTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'work', label: 'Work' },
  { id: 'people', label: 'People' },
  { id: 'code', label: 'Code' },
  { id: 'memory', label: 'Memory' },
  { id: 'rules', label: 'Rules' },
];

/** The parts of a tab that has a switch; the first is the default and stays out of the URL. */
export const PROJECT_PARTS: Partial<Record<ProjectTab, Array<{ id: string; label: string }>>> = {
  overview: [
    { id: 'summary', label: 'Summary' },
    { id: 'inbox', label: 'Inbox' },
    { id: 'activity', label: 'Activity' },
    { id: 'history', label: 'History' },
  ],
  work: [
    { id: 'board', label: 'Work' },
    { id: 'tasks', label: 'Board' },
    { id: 'plans', label: 'Plans' },
    { id: 'teamwork', label: 'Team Work' },
    { id: 'map', label: 'Map' },
    { id: 'risks', label: 'Risks' },
    { id: 'incidents', label: 'Incidents' },
  ],
  memory: [
    { id: 'knowledge', label: 'Knowledge' },
    { id: 'browse', label: 'Index' },
    { id: 'explore', label: 'Explore' },
    { id: 'labels', label: 'Labels' },
    { id: 'readers', label: 'Who can read' },
    { id: 'sharing', label: 'Sharing' },
  ],
  code: [
    { id: 'repositories', label: 'Repositories' },
    { id: 'history', label: 'History' },
    { id: 'compute', label: 'Compute' },
  ],
};

/** The old tab keys, mapped to where their content lives now. */
const LEGACY: Record<string, { tab: ProjectTab; part?: string }> = {
  inbox: { tab: 'overview', part: 'inbox' },
  activity: { tab: 'overview', part: 'activity' },
  members: { tab: 'people' },
  teams: { tab: 'people' },
  source: { tab: 'code', part: 'repositories' },
  trackers: { tab: 'code', part: 'repositories' },
  roots: { tab: 'code', part: 'repositories' },
  repositories: { tab: 'code', part: 'repositories' },
  compute: { tab: 'code', part: 'compute' },
  map: { tab: 'work', part: 'map' },
  risks: { tab: 'work', part: 'risks' },
  incidents: { tab: 'work', part: 'incidents' },
  policy: { tab: 'rules' },
};

/** Where the URL points: a current tab and part, an old tab key, or the overview. */
export function resolveProjectTab(params: URLSearchParams): { tab: ProjectTab; part: string } {
  const raw = params.get('tab') ?? '';
  const known = PROJECT_TABS.find((t) => t.id === raw)?.id;
  // No tab is the overview (projectTabParams leaves it out), and its part still counts: `?part=history&run=…`.
  const target = known ? { tab: known, part: params.get('part') ?? undefined } : raw === '' ? { tab: 'overview' as ProjectTab, part: params.get('part') ?? undefined } : (LEGACY[raw] ?? { tab: 'overview' as ProjectTab });
  const parts = PROJECT_PARTS[target.tab];
  const part = parts?.find((p) => p.id === target.part)?.id ?? parts?.[0]?.id ?? '';
  return { tab: target.tab, part };
}

/** The search params for a tab and part, leaving defaults out so the plain address is the overview. */
export function projectTabParams(tab: ProjectTab, part?: string): Record<string, string> {
  const parts = PROJECT_PARTS[tab];
  const out: Record<string, string> = {};
  if (tab !== 'overview') out.tab = tab;
  if (part && parts && part !== parts[0]?.id) out.part = part;
  return out;
}

/** A tab and part as the key the project's nav marks as current. */
export const projectNavKey = (tab: ProjectTab, part: string) => `${tab}:${part}`;

/** What a tab with no parts is called in the nav. */
const SINGLE: Partial<Record<ProjectTab, string>> = { people: 'Members and teams', rules: 'Policy and rules' };

/**
 * The project's sections for the SubnavLayout (Layout.tsx): a group per tab, a link per part, so a
 * part can be bookmarked, opened in a new tab and reached with Back. A tab with no parts is one link.
 */
export function projectNav(projectId: string): SubnavGroup[] {
  const path = `/projects/${encodeURIComponent(projectId)}`;
  const to = (tab: ProjectTab, part?: string) => {
    const q = new URLSearchParams(projectTabParams(tab, part)).toString();
    return q ? `${path}?${q}` : path;
  };
  return PROJECT_TABS.map((t) => {
    const parts = PROJECT_PARTS[t.id];
    return {
      label: t.label,
      items: parts ? parts.map((p) => ({ key: projectNavKey(t.id, p.id), label: p.label, to: to(t.id, p.id) })) : [{ key: projectNavKey(t.id, ''), label: SINGLE[t.id] ?? t.label, to: to(t.id) }],
    };
  });
}
